import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

// Stores
import { useAuth } from '@/stores/auth';
import { initializeSeasonStore } from '@/stores/season';

// Layout and protection
import { MainLayout } from '@/components/Layout/MainLayout';
import { ProtectedRoute, UnauthorizedPage } from '@/components/ProtectedRoute';

// Utils
import { queryClient } from '@/lib/queryClient';
import { connectionMonitor } from '@/lib/queryClient';
import AdminUsersPage from '@/pages/admin/AdminUsersPage.tsx';
import { applyTheme, getStoredTheme } from '@/lib/theme';

// Lazy-loaded pages for code splitting
const LoginPage = React.lazy(() => import('@/pages/auth/LoginPage'));
const DashboardPage = React.lazy(() => import('@/pages/dashboard/DashboardPage'));

// 🔥 NUOVA PAGINA UNIFICATA
const UnifiedGarePage = React.lazy(() => import('@/pages/utente/UnifiedGarePage'));
const StatisticheUserPage = React.lazy(() => import('@/pages/utente/StatisticheUserPage'));

// Kit gara (rapportino + sanzioni)
const InserimentoRapportinoPage = React.lazy(() => import('@/pages/utente/InserimentoRapportinoPage'));

const VisualizzaRapportinoPage = React.lazy(() => import('@/pages/utente/VisualizzaRapportinoPage'));
const InserisciCRA01Page = React.lazy(() => import('@/pages/admin/InserisciCRA01Page'));
const InserisciUtentiPage = React.lazy(() => import('@/pages/admin/InserisciUtentiPage'));
const StatistichePage = React.lazy(() => import('@/pages/admin/StatistichePage'));
const AdminListaGarePage = React.lazy(() => import('@/pages/admin/AdminListaGarePage'));
const MonitoraggioRapportiniPage = React.lazy(() => import('@/pages/admin/MonitoraggioRapportiniPage'));
const RichiesteRiaperturaRapportiniPage = React.lazy(() => import('@/pages/admin/RichiesteRiaperturaRapportiniPage'));
const MonitoraggioUsoPortalePage = React.lazy(() => import('@/pages/admin/MonitoraggioUsoPortalePage'));
const ImpostazioniPage = React.lazy(() => import('@/pages/admin/ImpostazioniPage'));
const ProfiloPage = React.lazy(() => import('@/pages/profilo/ProfiloPage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFoundPage'));

// Loading component
const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
  </div>
);

// Route loader component
const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
  </div>
);

// App initialization component
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, refreshUser } = useAuth();

useEffect(() => {
  const cleanup = connectionMonitor.setupListeners();
  applyTheme(getStoredTheme());
  const hasAccessToken = !!localStorage.getItem('arma4you.token');
  if (isAuthenticated && hasAccessToken) {
    initializeSeasonStore();
    refreshUser();
  }
  return cleanup;
}, [isAuthenticated, refreshUser]);

  return <>{children}</>;
};

// Main App component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppInitializer>
          <div className="App">
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />

                {/* Protected routes with layout */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Dashboard */}
                  <Route
                    index
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <DashboardPage />
                      </Suspense>
                    }
                  />

                  {/* 🔥 PAGINA UNIFICATA GARE */}
                  <Route
                    path="utente/gare"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <UnifiedGarePage />
                      </Suspense>
                    }
                  />
                  {/* 🔥 PAGINA STATISTICHE */}
                  <Route
                    path="utente/statistiche"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <StatisticheUserPage />
                      </Suspense>
                    }
                  />


                  {/* 🔄 REDIRECT DELLE VECCHIE ROUTE ALLA NUOVA PAGINA UNIFICATA */}
                  <Route
                    path="utente/lista-gare"
                    element={<Navigate to="/utente/gare" replace />}
                  />
                  <Route
                    path="utente/visualizza-gare"
                    element={<Navigate to="/utente/gare" replace />}
                  />
                  <Route
                    path="utente/gare-assegnate"
                    element={<Navigate to="/utente/gare" replace />}
                  />

                  {/* 🔄 RETROCOMPATIBILITÀ: vecchia rotta rapportino reindirizza alla lista */}
                  <Route
                    path="utente/inserimento-rapportino"
                    element={<Navigate to="/utente/gare" replace />}
                  />

                  {/* Rapportino (sempre con ID GARA interno) */}
                  <Route
                    path="utente/rapportino/:id"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <InserimentoRapportinoPage />
                      </Suspense>
                    }
                  />

                  <Route
                    path="utente/visualizza-rapportino/:id"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <VisualizzaRapportinoPage />
                      </Suspense>
                    }
                  />
                  
                  {/* Admin section - role-protected */}
                  <Route
                    path="admin/inserisci-cra01"
                    element={
                      <ProtectedRoute roles={['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']}>
                        <Suspense fallback={<PageLoader />}>
                          <InserisciCRA01Page />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/inserisci-utenti"
                    element={
                      <ProtectedRoute roles={['ref_informatico', 'presidente']}>
                        <Suspense fallback={<PageLoader />}>
                          <InserisciUtentiPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/statistiche"
                    element={
                      <ProtectedRoute roles={['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']}>
                        <Suspense fallback={<PageLoader />}>
                          <StatistichePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/monitoraggio-rapportini"
                    element={
                      <ProtectedRoute roles={['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']}>
                        <Suspense fallback={<PageLoader />}>
                          <MonitoraggioRapportiniPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/rapportini-riaperture"
                    element={
                      <ProtectedRoute roles={['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']}>
                        <Suspense fallback={<PageLoader />}>
                          <RichiesteRiaperturaRapportiniPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/monitoraggio-uso-portale"
                    element={
                      <ProtectedRoute roles={['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente']}>
                        <Suspense fallback={<PageLoader />}>
                          <MonitoraggioUsoPortalePage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/impostazioni"
                    element={
                      <ProtectedRoute roles={['ref_informatico']}>
                        <Suspense fallback={<PageLoader />}>
                          <ImpostazioniPage />
                        </Suspense>
                      </ProtectedRoute>
                    }
                  />
                  <Route
  path="/admin/gare"
  element={
    <ProtectedRoute roles={['organo_tecnico','ref_informatico','presidente','vice_presidente']}>
      <AdminListaGarePage />
    </ProtectedRoute>
  }
/>
<Route
  path="/admin/utenti"
  element={
    <ProtectedRoute roles={['ref_informatico','presidente','vice_presidente','organo_tecnico']}>
      <AdminUsersPage />
    </ProtectedRoute>
  }
/>


                  {/* Profile */}
                  <Route
                    path="profilo"
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <ProfiloPage />
                      </Suspense>
                    }
                  />
                </Route>

                {/* 404 page */}
                <Route
                  path="*"
                  element={
                    <Suspense fallback={<LoadingSpinner />}>
                      <NotFoundPage />
                    </Suspense>
                  }
                />
              </Routes>
            </Suspense>

            {/* Global components */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#333',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow:
                    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
                success: {
                  iconTheme: { primary: '#10b981', secondary: '#fff' },
                },
                error: {
                  iconTheme: { primary: '#ef4444', secondary: '#fff' },
                },
              }}
            />
          </div>
        </AppInitializer>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
